import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  TableCell, TableRow, Chip, IconButton,
  FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, InputAdornment, CircularProgress
} from '@mui/material';
import {
  Add, Search, SettingsOutlined, Warning
} from '@mui/icons-material';
import { fetchUsers, fetchRoles, createUser, updateUser, deleteUser } from '../../store/userSlice';
import { logout, updateUserRole } from '../../store/authSlice';
import { readPersistedSession, writePersistedSession } from '../../shared/authSession';
import { COLORS } from '../../shared/colors';
import { SIZING } from '../../shared/globalSX';
import { REGEX } from '../../shared/regex';
import { ICONS } from '../../shared/icons';
import { 
  BUTTON_TEXT, VALIDATION_MESSAGES, FORM_FIELDS, DATE_FORMAT_OPTIONS, TOAST_MESSAGES, STORAGE_KEYS, MANAGEMENT_FILTER_STATUS
} from '../../shared/appConstants';
import { getFieldLimit, handleCharacterLimit, handleCharacterLimitKeyDown, handleCharacterLimitPaste, cleanDeletedEmail } from '../../shared/commonFunctions';
import Navbar from '../shared/Navbar';
import OverflowTooltip from '../../shared/OverflowTooltip';
import CommonTable from '../shared/CommonTable';
import CommonDeleteConfirmDialog from '../shared/CommonDeleteConfirmDialog';
import CommonFormModal from '../shared/CommonFormModal';
import './UserManagement.css';
const dropdownMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300,
      width: 350,
      borderRadius: '12px',
      marginTop: '8px',
      boxShadow: '4px 4px 4px 0px #9EB3DB5E',
      border: '0.6px solid transparent',
      backgroundImage: 'linear-gradient(white, white), linear-gradient(179.65deg, #B7CBF2 0.33%, #5374B7 99.73%)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box'
    }
  }
};

const dropdownMenuItemSx = {
  py: 1.5,
  px: 2,
  mx: 1,
  borderRadius: '6px',
  fontSize: '0.875rem',
  gap: 1.5,
  borderBottom: '1.5px solid #E5E7EB',
  '&:hover': { bgcolor: COLORS.ui.menuHover },
  '&:last-of-type': {
    borderBottom: 'none'
  }
};

const UserManagement = () => {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSelfRoleChangeModal, setShowSelfRoleChangeModal] = useState(false);
  const [showSelfDeleteModal, setShowSelfDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const location = useLocation();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const searchParams = new URLSearchParams(location.search);
  const initialSearch = searchParams.get('search') || '';
  const statusParam = (searchParams.get('status') || '').toUpperCase();
  const roleParamKey = searchParams.get('role') || '';
  const [statusFilter, setStatusFilter] = useState(() => {
    if (statusParam === MANAGEMENT_FILTER_STATUS.ALL) return '';
    if ([MANAGEMENT_FILTER_STATUS.ACTIVE, MANAGEMENT_FILTER_STATUS.INACTIVE].includes(statusParam)) return statusParam;
    const savedStatus = sessionStorage.getItem(STORAGE_KEYS.USER_STATUS_FILTER) || '';
    return [MANAGEMENT_FILTER_STATUS.ACTIVE, MANAGEMENT_FILTER_STATUS.INACTIVE].includes(savedStatus) ? savedStatus : '';
  });
  const [roleFilter, setRoleFilter] = useState(() => {
    if (roleParamKey) return roleParamKey.split(',').filter(Boolean);
    return [];
  });

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(1);
  const tableContainerRef = useRef(null);
  const activeFetchRef = useRef(null);
  const isFetchingPageRef = useRef(false);
  const hasMoreRef = useRef(true);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    send_mail: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [formCharacterWarnings, setFormCharacterWarnings] = useState({});
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    status: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [editFormCharacterWarnings, setEditFormCharacterWarnings] = useState({});
  const [editFormUserInteracted, setEditFormUserInteracted] = useState({});
  const { user } = useSelector((state) => state.auth);
  const { users, roles, totalUsers, loading } = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const limit = 20;

  const isEditUserUnchanged = React.useMemo(() => {
    if (!selectedUser) return true;
    const normalizeUserForm = (data) => ({
      first_name: data?.first_name || '',
      last_name: data?.last_name || '',
      email: data?.email || '',
      role: data?.role || '',
      status: data?.status || ''
    });

    return JSON.stringify(normalizeUserForm(editFormData)) === JSON.stringify(normalizeUserForm(selectedUser));
  }, [editFormData, selectedUser]);

  const fetchUsersData = useCallback(async () => {
    const isFirstPage = currentPage === 1;
    if (isFirstPage) {
      // Only abort previous fetch when resetting (filter/search change), not when paginating
      if (activeFetchRef.current) {
        activeFetchRef.current.abort();
      }
      hasMoreRef.current = true;
    }
    if (!hasMoreRef.current) return;
    isFetchingPageRef.current = true;
    const promise = dispatch(fetchUsers({ page: currentPage, limit, name: debouncedSearchTerm, status: statusFilter, role: roleFilter }));
    activeFetchRef.current = promise;
    try {
      const result = await promise;
      if (result?.error) {
        if (result.error.name !== 'AbortError' && result.error.message !== 'Aborted') {
          console.error('Error fetching users:', result.error);
        }
      } else if (result?.payload && typeof result.payload === 'object') {
        const fetchedUsers = Array.isArray(result.payload.users) ? result.payload.users : [];
        const total = typeof result.payload.total === 'number' ? result.payload.total : 0;
        const loadedCount = currentPage === 1 ? fetchedUsers.length : (currentPage - 1) * limit + fetchedUsers.length;
        if (fetchedUsers.length === 0 || loadedCount >= total) {
          hasMoreRef.current = false;
        }
      } else if (result && (!result.payload || typeof result.payload !== 'object')) {
        console.warn('Invalid users data received:', result.payload);
      }
    } catch (error) {
      // ignore aborted promise rejection
    } finally {
      isFetchingPageRef.current = false;
    }
  }, [dispatch, currentPage, debouncedSearchTerm, statusFilter, roleFilter, limit]);

  const handleStatusFilterChange = (event) => {
    const nextStatus = event.target.value;
    setStatusFilter(nextStatus);
    sessionStorage.setItem(STORAGE_KEYS.USER_STATUS_FILTER, nextStatus);
    setCurrentPage(1);
    const nextParams = new URLSearchParams(urlSearchParams);
    if (nextStatus) nextParams.set('status', nextStatus);
    else nextParams.delete('status');
    setUrlSearchParams(nextParams, { replace: true });
  };

  const handleRoleFilterChange = (event) => {
    const selectedValues = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    const nextRoles = selectedValues.includes('') ? [] : selectedValues;
    setRoleFilter(nextRoles);
    setCurrentPage(1);
    const nextParams = new URLSearchParams(urlSearchParams);
    nextParams.delete('role');
    if (nextRoles.length) nextParams.set('role', nextRoles.join(','));
    setUrlSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    if (statusParam !== MANAGEMENT_FILTER_STATUS.ALL && ![MANAGEMENT_FILTER_STATUS.ACTIVE, MANAGEMENT_FILTER_STATUS.INACTIVE].includes(statusParam)) return;
    const nextStatus = statusParam === MANAGEMENT_FILTER_STATUS.ALL ? '' : statusParam;
    setStatusFilter((currentStatus) => currentStatus === nextStatus ? currentStatus : nextStatus);
    setCurrentPage((page) => page === 1 ? page : 1);
  }, [statusParam]);

  useEffect(() => {
    if (!roleParamKey) return;
    const nextRoles = roleParamKey.split(',').filter(Boolean);
    setRoleFilter((currentRoles) => currentRoles.join(',') === nextRoles.join(',') ? currentRoles : nextRoles);
    setCurrentPage((page) => page === 1 ? page : 1);
  }, [roleParamKey]);

  const refreshUsers = useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
    if (currentPage === 1) {
      fetchUsersData();
    } else {
      setCurrentPage(1);
    }
  }, [fetchUsersData, currentPage]);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  // Debounce search input and reset page to 1
  useEffect(() => {
    const timer = setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch users when page or debounced search term changes
  useEffect(() => {
    fetchUsersData();
  }, [fetchUsersData]);

  // Scroll listener for infinite scroll
  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current || loading || isFetchingPageRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      if (users.length > 0 && users.length < totalUsers && hasMoreRef.current) {
        isFetchingPageRef.current = true;
        setCurrentPage(prev => prev + 1);
      }
    }
  }, [loading, users.length, totalUsers]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Auto-load if the loaded users don't fill the container height and there are more users to fetch
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || loading || users.length === 0 || users.length >= totalUsers) return;

    // Only load if we have fully filled the pages up to the current page
    if (currentPage * limit <= users.length && container.scrollHeight <= container.clientHeight) {
      setCurrentPage(prev => prev + 1);
    }
  }, [users.length, totalUsers, loading, currentPage, limit]);



  const formatLastLogin = (lastLogin) => {
    const date = new Date(lastLogin);
    if (lastLogin === null || lastLogin === undefined || isNaN(date.getTime())) {
      return t("users.never");
    }
    return date.toLocaleString("en-US", DATE_FORMAT_OPTIONS);
  };

  const validateEmailFormat = (email) => {
    if (!email) return false;

    // Clean the email by removing all whitespace and invisible characters
    const cleanedEmail = email.replace(REGEX.emailWhitespace, '').replace(REGEX.emailInvisibleChars, '').trim();

    if (!cleanedEmail) return false;

    // Check basic email format
    if (!REGEX.email.test(cleanedEmail)) return false;

    // Check if email starts or ends with dot
    if (cleanedEmail.startsWith('.') || cleanedEmail.endsWith('.')) return false;

    // Check for consecutive dots
    if (cleanedEmail.includes('..')) return false;

    // Check if local part (before @) starts or ends with dot
    const [localPart, domainPart] = cleanedEmail.split('@');
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;

    // Check if domain part has consecutive dots
    if (domainPart && domainPart.includes('..')) return false;

    return true;
  };


  const validateForm = () => {
    const errors = {};

    if (!formData[FORM_FIELDS.FIRST_NAME].trim()) {
      errors[FORM_FIELDS.FIRST_NAME] = VALIDATION_MESSAGES.FIRST_NAME_REQUIRED;
    }

    if (!formData[FORM_FIELDS.LAST_NAME].trim()) {
      errors[FORM_FIELDS.LAST_NAME] = VALIDATION_MESSAGES.LAST_NAME_REQUIRED;
    }

    if (!formData[FORM_FIELDS.EMAIL].trim()) {
      errors[FORM_FIELDS.EMAIL] = VALIDATION_MESSAGES.EMAIL_REQUIRED;
    } else if (!validateEmailFormat(formData[FORM_FIELDS.EMAIL])) {
      errors[FORM_FIELDS.EMAIL] = VALIDATION_MESSAGES.VALID_EMAIL_REQUIRED;
    }

    if (!formData[FORM_FIELDS.ROLE]) {
      errors[FORM_FIELDS.ROLE] = 'Role is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };



  const openAddUser = () => {
    setFormData({ first_name: '', last_name: '', email: '', role: '', send_mail: false });
    setShowAddModal(true);
  };
  const closeAddUser = (options = {}) => {
    const force = options?.force === true;
    if (isCreating && !force) return;
    setShowAddModal(false);
    setFormData({ first_name: '', last_name: '', email: '', role: '', send_mail: false });
    setFormErrors({});
    setFormCharacterWarnings({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let fieldValue = type === 'checkbox' ? checked : value;

    // Clean email field of invisible characters when pasted
    if (name === 'email' && type !== 'checkbox') {
      fieldValue = value.replace(REGEX.emailWhitespace, '').replace(REGEX.emailInvisibleChars, '').trim();
    }

    // Check character limits for text fields
    if (type !== 'checkbox') {
      const { value: limitedValue, showWarning } = handleCharacterLimit(name, fieldValue);
      fieldValue = limitedValue;

      const newWarnings = { ...formCharacterWarnings };
      if (showWarning) {
        const limit = getFieldLimit(name);
        newWarnings[name] = t('characterLimits.warningText', { limit: limit?.toLocaleString() });
      } else {
        delete newWarnings[name];
      }
      setFormCharacterWarnings(newWarnings);
    }

    setFormData(prev => ({ ...prev, [name]: fieldValue }));

    // Real-time validation and error clearing
    const newErrors = { ...formErrors };

    if (name === FORM_FIELDS.EMAIL && fieldValue.trim()) {
      if (!validateEmailFormat(fieldValue)) {
        newErrors[FORM_FIELDS.EMAIL] = VALIDATION_MESSAGES.VALID_EMAIL_REQUIRED;
      } else {
        delete newErrors[FORM_FIELDS.EMAIL];
      }
    } else if (name === FORM_FIELDS.FIRST_NAME && fieldValue.trim()) {
      delete newErrors[FORM_FIELDS.FIRST_NAME];
    } else if (name === FORM_FIELDS.LAST_NAME && fieldValue.trim()) {
      delete newErrors[FORM_FIELDS.LAST_NAME];
    } else if (name === FORM_FIELDS.ROLE && fieldValue) {
      delete newErrors[FORM_FIELDS.ROLE];
    }

    setFormErrors(newErrors);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;

    // Mark as user interacted
    setEditFormUserInteracted(prev => ({ ...prev, [name]: true }));

    let fieldValue = value;

    // Clean email field of invisible characters when pasted
    if (name === 'email') {
      fieldValue = value.replace(REGEX.emailWhitespace, '').replace(REGEX.emailInvisibleChars, '').trim();
    }

    const { value: processedValue, showWarning } = handleCharacterLimit(
      name,
      fieldValue,
      editFormUserInteracted[name]
    );

    const newWarnings = { ...editFormCharacterWarnings };
    if (showWarning) {
      const limit = getFieldLimit(name);
      newWarnings[name] = t('characterLimits.warningText', { limit: limit?.toLocaleString() });
    } else {
      delete newWarnings[name];
    }
    setEditFormCharacterWarnings(newWarnings);

    setEditFormData(prev => ({ ...prev, [name]: processedValue }));

    // Real-time validation and error clearing
    const newErrors = { ...editFormErrors };

    if (name === FORM_FIELDS.EMAIL && processedValue.trim()) {
      if (!validateEmailFormat(processedValue)) {
        newErrors[FORM_FIELDS.EMAIL] = VALIDATION_MESSAGES.VALID_EMAIL_REQUIRED;
      } else {
        delete newErrors[FORM_FIELDS.EMAIL];
      }
    } else if (name === FORM_FIELDS.FIRST_NAME && processedValue.trim()) {
      delete newErrors[FORM_FIELDS.FIRST_NAME];
    } else if (name === FORM_FIELDS.LAST_NAME && processedValue.trim()) {
      delete newErrors[FORM_FIELDS.LAST_NAME];
    }

    setEditFormErrors(newErrors);
  };

  const handleKeyDown = (e, fieldName, isEdit = false) => {
    const currentValue = isEdit ? editFormData[fieldName] : formData[fieldName];

    // Mark as user interacted for edit form
    if (isEdit && e.key.length === 1) {
      setEditFormUserInteracted(prev => ({ ...prev, [fieldName]: true }));
    }

    // Use common function for character limit handling
    handleCharacterLimitKeyDown(
      e,
      fieldName,
      currentValue,
      (warningText) => {
        if (isEdit) {
          const limit = getFieldLimit(fieldName);
          const newWarnings = { ...editFormCharacterWarnings };
          newWarnings[fieldName] = t('characterLimits.warningText', { limit: limit?.toLocaleString() });
          setEditFormCharacterWarnings(newWarnings);
        }
      }
    );
  };

  const handlePaste = (e, fieldName, isEdit = false) => {
    const currentValue = isEdit ? editFormData[fieldName] : formData[fieldName];

    // Use common function for character limit paste handling
    const wasHandled = handleCharacterLimitPaste(
      e,
      fieldName,
      currentValue,
      (truncatedPaste) => {
        const input = e.target;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const beforeCursor = currentValue.substring(0, start);
        const afterCursor = currentValue.substring(end);
        const newValue = beforeCursor + truncatedPaste + afterCursor;

        const syntheticEvent = {
          target: {
            name: fieldName,
            value: newValue,
            type: 'text'
          }
        };

        if (isEdit) {
          handleEditInputChange(syntheticEvent);
        } else {
          handleInputChange(syntheticEvent);
        }
      }
    );

    // If not handled by common function, allow default paste behavior
    if (!wasHandled) {
      // Let the default paste behavior handle it
      return;
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!validateForm() || isCreating) {
      return;
    }

    setIsCreating(true);
    try {
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        role: formData.role,
        workspace_id: user?.workspace_id,
        send_mail: formData.send_mail
      };

      await dispatch(createUser(userData)).unwrap();
      toast.success('User created successfully!');
      closeAddUser({ force: true });
      refreshUsers();
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.USER_CREATE_FAILED;
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const openEditUser = (userData) => {
    setSelectedUser(userData);
    setEditFormData({
      first_name: userData.first_name,
      last_name: userData.last_name,
      email: userData.email,
      role: userData.role,
      status: userData.status
    });
    setShowEditModal(true);
  };
  const closeEditUser = (options = {}) => {
    const force = options?.force === true;
    if (isUpdating && !force) return;
    setShowEditModal(false);
    setSelectedUser(null);
    setEditFormData({ first_name: '', last_name: '', email: '', role: '', status: '' });
    setEditFormErrors({});
    setEditFormCharacterWarnings({});
    setEditFormUserInteracted({});
  };



  const validateEditForm = () => {
    const errors = {};

    if (!editFormData[FORM_FIELDS.FIRST_NAME].trim()) {
      errors[FORM_FIELDS.FIRST_NAME] = VALIDATION_MESSAGES.FIRST_NAME_REQUIRED;
    }

    if (!editFormData[FORM_FIELDS.LAST_NAME].trim()) {
      errors[FORM_FIELDS.LAST_NAME] = VALIDATION_MESSAGES.LAST_NAME_REQUIRED;
    }

    if (!editFormData[FORM_FIELDS.EMAIL].trim()) {
      errors[FORM_FIELDS.EMAIL] = VALIDATION_MESSAGES.EMAIL_REQUIRED;
    } else if (!validateEmailFormat(editFormData[FORM_FIELDS.EMAIL])) {
      errors[FORM_FIELDS.EMAIL] = VALIDATION_MESSAGES.VALID_EMAIL_REQUIRED;
    }

    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    if (isUpdating || isEditUserUnchanged) {
      return;
    }

    if (!validateEditForm()) {
      return;
    }

    // Check if user is changing their own role
    const isSelfRoleChange = selectedUser.id === user.id &&
      String(editFormData.role || '').trim().toLowerCase() !== String(selectedUser.role || '').trim().toLowerCase();

    if (isSelfRoleChange) {
      setShowSelfRoleChangeModal(true);
      return;
    }

    await performUserUpdate();
  };

  const performUserUpdate = async () => {
    if (isUpdating || isEditUserUnchanged) return;

    setIsUpdating(true);
    try {
      const userData = {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        email: editFormData.email,
        role: editFormData.role,
        status: editFormData.status
      };

      await dispatch(updateUser({ id: selectedUser.id, data: userData })).unwrap();
      toast.success('User updated successfully!');

      // If user changed their own role, synchronize role and log them out
      const isSelfRoleChanged = selectedUser.id === user.id &&
        String(editFormData.role || '').trim().toLowerCase() !== String(selectedUser.role || '').trim().toLowerCase();

      if (selectedUser.id === user.id) {
        const nextRole = String(editFormData.role || '').trim().toLowerCase();
        dispatch(updateUserRole(nextRole));
        const session = readPersistedSession();
        if (session?.user) {
          writePersistedSession({
            ...session,
            user: { ...session.user, role: nextRole }
          });
        }
      }

      if (isSelfRoleChanged) {
        setTimeout(() => {
          dispatch(logout());
          toast.info('You have been logged out due to role change.');
        }, 1500);
      }

      closeEditUser({ force: true });
      refreshUsers();
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.USER_UPDATE_FAILED;
      toast.error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleConfirmSelfRoleChange = async () => {
    setShowSelfRoleChangeModal(false);
    await performUserUpdate();
  };

  const handleCancelSelfRoleChange = () => {
    setShowSelfRoleChangeModal(false);
  };

  const openDeleteUser = (userId, userName) => {
    setSelectedUser({ id: userId, name: userName });
    if (userId === user?.id) {
      setShowSelfDeleteModal(true);
    } else {
      setShowDeleteModal(true);
    }
  };
  const closeDeleteUser = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const handleCancelSelfDelete = () => {
    if (isDeleting) return;
    setShowSelfDeleteModal(false);
    setSelectedUser(null);
  };

  const confirmDeleteUser = async () => {
    try {
      await dispatch(deleteUser(selectedUser.id)).unwrap();
      toast.success('User deleted successfully!');
      closeDeleteUser();
      refreshUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user. Please try again.');
      throw error;
    }
  };

  const handleConfirmSelfDelete = async () => {
    if (isDeleting || !selectedUser) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteUser(selectedUser.id)).unwrap();
      toast.success('User deleted successfully!');
      setShowSelfDeleteModal(false);
      setSelectedUser(null);
      dispatch(logout());
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };



  const tableColumns = [
    { id: 'user', label: t("users.user"), width: '24%', maxWidth: '150px' },
    { id: 'email', label: t("users.email"), width: '24%', maxWidth: '500px' },
    { id: 'role', label: t("users.role"), width: '14%' },
    { id: 'status', label: t("common.status"), width: '12%' },
    { id: 'lastLogin', label: t("users.lastLogin"), width: '16%' },
    { id: 'actions', label: '', width: '10%' }
  ];

  const renderTableRow = (userData, index) => (
    <TableRow key={userData.id}>
      <TableCell sx={{ width: '25%', maxWidth: '150px' }}>
        <OverflowTooltip
          title={`${userData.first_name} ${userData.last_name}`}
          variant="body2"
          tooltipWidth={500}
          placement="top-start"
        />
      </TableCell>
      <TableCell sx={{ width: '25%', maxWidth: '500px' }}>
        <OverflowTooltip
          title={cleanDeletedEmail(userData.email)}
          variant="body2"
          tooltipWidth={500}
          placement="top-start"
        />
      </TableCell>
      <TableCell sx={{ textTransform: 'capitalize' }}>{userData.role}</TableCell>
      <TableCell>
        <Chip
          label={userData.status === 'SOFT-DELETE' ? 'Deleted' : userData.status}
          color={userData.status.toLowerCase() === 'active' ? 'success' : 'default'}
          size="small"
          sx={{ 
              width: 'fit-content',
              bgcolor: userData.status.toLowerCase() === 'active' ? COLORS.successLight : 'rgba(239, 68, 68, 0.1)',
              color: userData.status.toLowerCase() === 'active' ? COLORS.successDark : COLORS.error,
              border: `1px solid ${userData.status.toLowerCase() === 'active' ? COLORS.successDark : COLORS.error}`,
              fontWeight: 600
          }}
        />
      </TableCell>
      <TableCell>{formatLastLogin(userData.last_login)}</TableCell>
      <TableCell align="right">
        <IconButton size="small" onClick={() => openEditUser(userData)}>
          <img src={ICONS.editBlue} alt="Edit" style={{ width: '18px', height: '18px' }} />
        </IconButton>
        <IconButton size="small" onClick={() => openDeleteUser(userData.id, `${userData.first_name} ${userData.last_name}`)}>
          <img src={ICONS.deleteRed} alt="Delete" style={{ width: '18px', height: '18px' }} />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="dashboard">

      <Navbar />


      {/* Main Content */}
      <main className="main" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Top Header - Just the title */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1, borderRadius: '8px' }}>
          <Typography variant="h6" fontWeight={600} color={COLORS.text.muted}>
            {t('users.userManagement')}
          </Typography>
        </Box>

        {/* Action Row - All Users, Search, Add Button */}
        <Box className="user-management-header" sx={{ mb: SIZING.margin.md, bgcolor: COLORS.tableHeaderBg, borderBottom: `1px solid ${COLORS.brand.primaryDark}`, py: '0.25rem' }}>
            <Typography variant="h6" fontWeight="700" sx={{ color: COLORS.brand.primaryDark, fontSize: SIZING.font.md, m: 0 }}>
                {t("users.allUsers")}
            </Typography>
            <Box display="flex" gap={SIZING.gap.sm}>
                <FormControl size="small" sx={{ minWidth: 140, bgcolor: COLORS.white }}>
                    <InputLabel shrink>{t('common.status')}</InputLabel>
                    <Select
                        value={statusFilter}
                        label={t('common.status')}
                        onChange={handleStatusFilterChange}
                        displayEmpty
                        renderValue={(value) => {
                            if (value === MANAGEMENT_FILTER_STATUS.ACTIVE) return t('common.active', 'Active');
                            if (value === MANAGEMENT_FILTER_STATUS.INACTIVE) return t('common.inactive', 'Inactive');
                            return t('common.all', 'All');
                        }}
                        inputProps={{ 'aria-label': t('common.status') }}
                    >
                        <MenuItem value="">{t('common.all', 'All')}</MenuItem>
                        <MenuItem value={MANAGEMENT_FILTER_STATUS.ACTIVE}>{t('common.active', 'Active')}</MenuItem>
                        <MenuItem value={MANAGEMENT_FILTER_STATUS.INACTIVE}>{t('common.inactive', 'Inactive')}</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140, bgcolor: COLORS.white }}>
                    <InputLabel shrink>{t('users.role')}</InputLabel>
                    <Select
                        multiple
                        value={roleFilter}
                        label={t('users.role')}
                        onChange={handleRoleFilterChange}
                        displayEmpty
                        renderValue={(values) => {
                          const allRolesSelected = roles.length > 0 && values.length === roles.length;
                          if (!values.length || allRolesSelected) return t('common.all', 'All');
                          return values
                            .map(value => value.toLowerCase() === 'pm' ? 'PM' : value.charAt(0).toUpperCase() + value.slice(1))
                            .join(', ');
                        }}
                        inputProps={{ 'aria-label': t('users.role') }}
                    >
                        <MenuItem value="">
                            <Checkbox checked={roleFilter.length === 0 || (roles.length > 0 && roleFilter.length === roles.length)} />
                            {t('common.all', 'All')}
                        </MenuItem>
                        {roles.map(role => (
                            <MenuItem key={role.id} value={role.name}>
                                <Checkbox checked={roleFilter.includes(role.name)} />
                                {role.name.toLowerCase() === 'pm' ? 'PM' : role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    placeholder={t("users.searchUsers")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="user-search-input pm-action-search"
                    sx={{ bgcolor: COLORS.white, '& .MuiOutlinedInput-root': { bgcolor: COLORS.white } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                            <Search sx={{ fontSize: 20, color: COLORS.brand.primaryDark }} />
                            </InputAdornment>
                        )
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openAddUser}
                    className="pm-action-button"
                    sx={{ bgcolor: COLORS.ui.buttonPaleBlue, color: COLORS.brand.primaryDark, boxShadow: 'none', '&:hover': { bgcolor: COLORS.ui.buttonPaleBlueHover, boxShadow: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: '4px' }}
                >
                    {t('users.addUser')}
                </Button>
            </Box>
        </Box>

        <CommonTable
            columns={tableColumns}
            data={users}
            renderRow={renderTableRow}
            loading={loading}
            emptyMessage={t("users.noUsersFound")}
            currentPage={currentPage}
            limit={limit}
            totalItems={totalUsers}
            preserveRowsOnLoading={true}
            showLoadingMore={users.length < totalUsers}
            tableContainerRef={tableContainerRef}
            tableContainerSx={{ flex: 1, minHeight: 0 }}
            cardSx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        />
      </main>

      {/* Add User Modal */}
      <CommonFormModal
        open={showAddModal}
        onClose={closeAddUser}
        title={t("users.addNewUser")}
        subtitle={t("users.createNewUser", "Create a new account")}
        onSubmit={handleCreateUser}
        submitLabel={isCreating ? BUTTON_TEXT.CREATING : t("users.createUserButton", "Create User")}
        cancelLabel={t("common.cancel")}
        isSubmitting={isCreating}
      >
        <Typography className="pm-dialog-section-title">
          {t("users.personalInformation")}
          <Box
            component="img"
            src={ICONS.userIcon}
            alt=""
            aria-hidden="true"
            sx={{ width: 20, height: 20 }}
          />
        </Typography>
        <Box className="pm-form-grid">
          <Box className="pm-form-field">
            <TextField
              fullWidth
              label={t("users.firstName")}
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, 'first_name')}
              onPaste={(e) => handlePaste(e, 'first_name')}
              required
              variant="outlined"
              error={!!formErrors.first_name}
              helperText={formErrors.first_name || formCharacterWarnings.first_name}
            />
          </Box>
          <Box className="pm-form-field">
            <TextField
              fullWidth
              label={t("users.lastName")}
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, 'last_name')}
              onPaste={(e) => handlePaste(e, 'last_name')}
              required
              variant="outlined"
              error={!!formErrors.last_name}
              helperText={formErrors.last_name || formCharacterWarnings.last_name}
            />
          </Box>

          <Box className="pm-form-field">
            <TextField
              fullWidth
              label={t("users.emailAddress")}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, 'email')}
              onPaste={(e) => handlePaste(e, 'email')}
              required
              variant="outlined"
              error={!!formErrors.email}
              helperText={formErrors.email || formCharacterWarnings.email}
            />
          </Box>
          <Box className="pm-form-field">
            <FormControl fullWidth variant="outlined" error={!!formErrors.role}>
              <InputLabel>{t("users.roleRequired", "Role")}</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                label={t("users.roleRequired", "Role")}
                required
                MenuProps={dropdownMenuProps}
              >
                {roles.map(role => (
                  <MenuItem key={role.id} value={role.name} sx={dropdownMenuItemSx}>
                    {role.name.toLowerCase() === 'pm' ? 'PM' : role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Typography className="pm-dialog-section-title">
          {t("users.additionalOptions", "Additional Options")} <SettingsOutlined color="action" />
        </Typography>
        <Box sx={{ pl: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                name="send_mail"
                checked={formData.send_mail}
                onChange={handleInputChange}
              />
            }
            label={t("users.sendWelcomeEmail")}
            sx={{ mb: 1 }}
          />
        </Box>
      </CommonFormModal>

      {/* Edit User Modal */}
      <CommonFormModal
        open={showEditModal}
        onClose={closeEditUser}
        title={t("users.editUserTitle")}
        subtitle={t("users.updateUserInfo")}
        onSubmit={handleUpdateUser}
        submitLabel={isUpdating ? t('common.updating', 'Updating...') : t("users.updateUserButton")}
        cancelLabel={t("common.cancel")}
        isSubmitting={isUpdating}
        isSubmitDisabled={isEditUserUnchanged}
      >
        <Typography className="pm-dialog-section-title">
          {t("users.personalInformation")}
          <Box component="img" src={ICONS.userIcon} alt="" aria-hidden="true" sx={{ width: 20, height: 20 }} />
        </Typography>
        <Box className="pm-form-grid">
          <Box className="pm-form-field">
            <TextField
              fullWidth
              label={t("users.firstName")}
              name="first_name"
              value={editFormData.first_name}
              onChange={handleEditInputChange}
              onKeyDown={(e) => handleKeyDown(e, 'first_name', true)}
              onPaste={(e) => handlePaste(e, 'first_name', true)}
              required
              variant="outlined"
              error={!!editFormErrors.first_name}
              helperText={editFormErrors.first_name || editFormCharacterWarnings.first_name}
            />
          </Box>
          <Box className="pm-form-field">
            <TextField
              fullWidth
              label={t("users.lastName")}
              name="last_name"
              value={editFormData.last_name}
              onChange={handleEditInputChange}
              onKeyDown={(e) => handleKeyDown(e, 'last_name', true)}
              onPaste={(e) => handlePaste(e, 'last_name', true)}
              required
              variant="outlined"
              error={!!editFormErrors.last_name}
              helperText={editFormErrors.last_name || editFormCharacterWarnings.last_name}
            />
          </Box>
          <Box className="pm-form-field">
            <TextField
              fullWidth
              label={t("users.emailAddress")}
              name="email"
              type="email"
              value={editFormData.email}
              onChange={handleEditInputChange}
              onKeyDown={(e) => handleKeyDown(e, 'email', true)}
              onPaste={(e) => handlePaste(e, 'email', true)}
              required
              variant="outlined"
              error={!!editFormErrors.email}
              helperText={editFormErrors.email || editFormCharacterWarnings.email}
            />
          </Box>
          <Box className="pm-form-field">
            <FormControl fullWidth variant="outlined">
              <InputLabel>{t("users.roleRequired", "Role")}</InputLabel>
              <Select
                name="role"
                value={editFormData.role}
                onChange={handleEditInputChange}
                label={t("users.roleRequired", "Role")}
                required
                MenuProps={dropdownMenuProps}
              >
                {roles.map(role => (
                  <MenuItem key={role.id} value={role.name} sx={dropdownMenuItemSx}>
                    {role.name.toLowerCase() === 'pm' ? 'PM' : role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Typography className="pm-dialog-section-title">
          {t("users.roleStatus")} <SettingsOutlined color="action" />
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: '1rem' }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>{t("users.statusRequired")}</InputLabel>
            <Select
              name="status"
              value={editFormData.status}
              onChange={handleEditInputChange}
              label={t("users.statusRequired")}
              required
              MenuProps={dropdownMenuProps}
            >
              <MenuItem value="ACTIVE" sx={dropdownMenuItemSx}>{t("common.active")}</MenuItem>
              <MenuItem value="INACTIVE" sx={dropdownMenuItemSx}>{t("common.inactive")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </CommonFormModal>

      <CommonDeleteConfirmDialog
        open={showDeleteModal}
        onClose={closeDeleteUser}
        onConfirm={confirmDeleteUser}
        title={t("users.deleteUserTitle")}
        question={t("users.deleteConfirmation")}
        itemName={selectedUser?.name}
        warning={t("users.deleteWarning")}
        confirmText={t("users.deleteUserButton")}
        cancelText={t("common.cancel")}
      />

      {/* Self Role Change Warning Modal */}
      <Dialog open={showSelfRoleChangeModal} onClose={handleCancelSelfRoleChange} maxWidth="sm">
        <DialogTitle sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning /> {t("users.roleChangeWarning")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t("users.roleChangeMessage", { currentRole: selectedUser?.role, newRole: editFormData.role })}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            <strong>{t("users.warning")}:</strong> {t("users.logoutWarning")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("users.reloginMessage")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSelfRoleChange} variant="outlined" disabled={isUpdating}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmSelfRoleChange}
            variant="contained"
            color="warning"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isUpdating ? t('common.updating', 'Updating...') : t("users.continueAndLogout")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Self Delete Warning Modal */}
      <Dialog
        open={showSelfDeleteModal}
        onClose={handleCancelSelfDelete}
        disableEscapeKeyDown={isDeleting}
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning /> {t("users.selfDeleteWarning")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t("users.selfDeleteMessage")}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            <strong>{t("users.warning")}:</strong> {t("users.selfDeleteLogoutWarning")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("users.selfDeleteReloginMessage")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSelfDelete} variant="outlined" disabled={isDeleting}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmSelfDelete}
            variant="contained"
            color="warning"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isDeleting ? t('common.deleting', 'Deleting...') : t("users.deleteAndLogout")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserManagement;
